import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
   Select,
   SelectContent,
   SelectItem,
   SelectTrigger,
   SelectValue,
} from "@/components/ui/select";
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { getUserId } from '@/utils/auth';
import { format } from "date-fns";
import { CalendarIcon, ChevronLeft, FileX, HelpCircle, RefreshCw, Search, XCircle } from 'lucide-react'; // Added Search icon import
import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import ClientApplicationCard from './components/ClientApplicationCard';
import UserOOPCard from './components/UserOOPCard';
import { useUserApplications } from './hooks/useUserApplications';
import { useUserOrderOfPayments } from './hooks/useUserOrderOfPayments';

// Number of cards to show per load
const CARDS_PER_PAGE = 9;

/**
 * ClientApplicationsPage
 * A modern, user-friendly interface for clients to monitor their application status
 * Features:
 * - Clean, card-based layout with infinite scrolling
 * - Easy filtering and search
 * - Visual status indicators
 * - Smooth animations and transitions
 */
const ClientApplicationsPage = () => {
   const [filters, setFilters] = useState({
      searchTerm: '',
      applicationType: 'all',
      dateRange: {
         from: undefined,
         to: undefined
      }
   });
   const [isRefreshing, setIsRefreshing] = useState(false);
   const [activeStatus, setActiveStatus] = useState('all');
   const [visibleCards, setVisibleCards] = useState(CARDS_PER_PAGE);
   // Add a loading state for pagination
   const [isLoadingMore, setIsLoadingMore] = useState(false);
   const userId = getUserId();
   const isMobile = useMediaQuery('(max-width: 640px)');
   const [viewType, setViewType] = useState('applications'); // 'applications' or 'oops'

   // Intersection Observer setup for infinite scrolling
   const { ref: loadMoreRef, inView } = useInView({
      threshold: 0,
      triggerOnce: false,
      // Add a small rootMargin to trigger the load a bit earlier
      rootMargin: '100px 0px'
   });

   // Initialize useUserApplications with null parameters to fetch all applications
   const {
      applications = [],
      loading,
      error,
      refetch,
      fetchUserApplications,
      deletePermit,
      unsubmitPermit,
      submitPermit,
      resubmitPermit,
      updateCSAWPermit,
      fetchCSAWPermit,
      updateCOVPermit,
      fetchCOVPermit,
      updatePTPRPermit,
      fetchPTPRPermit,
      updatePLTCPPermit,
      fetchPLTCPPermit,
      updatePLTPPermit,
      fetchPLTPPermit,
      updateTCEBPPermit,
      fetchTCEBPPermit,
   } = useUserApplications(null, null);

   // Add OOP query
   const { oops, loading: oopsLoading, error: oopsError, refetch: refetchOOPs } = useUserOrderOfPayments(userId);

   // Filter applications based on current filters and active status
   const filteredApplications = useMemo(() => {
      if (!applications) return [];

      return applications.filter(app => {
         const searchMatch = filters.searchTerm.toLowerCase() === '' ||
            app.applicationNumber?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
            app.applicationType?.toLowerCase().includes(filters.searchTerm.toLowerCase());

         const typeMatch = filters.applicationType === 'all' ||
            app.applicationType === filters.applicationType;

         const statusMatch = activeStatus === 'all' ||
            app.status === activeStatus;

         // Date range filtering
         const dateMatch = !filters.dateRange.from || !filters.dateRange.to || (
            app.dateOfSubmission &&
            new Date(app.dateOfSubmission) >= filters.dateRange.from &&
            new Date(app.dateOfSubmission) <= filters.dateRange.to
         );

         return searchMatch && typeMatch && statusMatch && dateMatch;
      });
   }, [applications, filters, activeStatus]);

   // Get visible applications based on current page
   const visibleApplications = useMemo(() => {
      return filteredApplications.slice(0, visibleCards);
   }, [filteredApplications, visibleCards]);

   // Load more handler - Update to prevent multiple loads
   const handleLoadMore = () => {
      if (isLoadingMore) return;
      setIsLoadingMore(true);

      // Use setTimeout to ensure the state updates properly
      setTimeout(() => {
         setVisibleCards(prev => prev + CARDS_PER_PAGE);
         setIsLoadingMore(false);
      }, 300);
   };

   // Effect to handle infinite scrolling - Update to check loading state
   useEffect(() => {
      if (inView && !isLoadingMore && visibleApplications.length < filteredApplications.length) {
         handleLoadMore();
      }
   }, [inView, isLoadingMore, visibleApplications.length, filteredApplications.length]);

   // Reset loading more state when filters change
   useEffect(() => {
      setIsLoadingMore(false);
   }, [filters, activeStatus]);

   // Reset visible cards when filters change
   useEffect(() => {
      setVisibleCards(CARDS_PER_PAGE);
   }, [filters, activeStatus]);

   // Add OOP filtering
   const filteredOOPs = useMemo(() => {
      if (!oops) return [];

      return oops.filter(oop => {
         const searchMatch = filters.searchTerm.toLowerCase() === '' ||
            oop.applicationNumber?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
            oop.billNo?.toLowerCase().includes(filters.searchTerm.toLowerCase());

         const dateMatch = !filters.dateRange.from || !filters.dateRange.to ||
            (new Date(oop.createdAt) >= filters.dateRange.from &&
               new Date(oop.createdAt) <= filters.dateRange.to);

         const statusMatch = activeStatus === 'all' ||
            oop.OOPstatus === activeStatus;

         return searchMatch && dateMatch && statusMatch;
      });
   }, [oops, filters, activeStatus]);

   // Update the main content rendering
   const renderMainContent = () => {
      if (viewType === 'oops') {
         if (oopsLoading) {
            return (
               <div className="flex justify-center items-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-green-600" />
                  <p className="ml-3 text-gray-500">Loading order of payments...</p>
               </div>
            );
         }

         if (oopsError) {
            return (
               <div className="flex flex-col items-center justify-center py-12">
                  <XCircle className="h-12 w-12 text-red-500 mb-4" />
                  <p className="text-gray-600">Error loading order of payments: {oopsError.message}</p>
               </div>
            );
         }

         if (!filteredOOPs || filteredOOPs.length === 0) {
            return (
               <div className="flex flex-col items-center justify-center py-12">
                  <FileX className="h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No order of payments found</h3>
                  <p className="text-sm text-gray-500">Try adjusting your filters or create a new application</p>
               </div>
            );
         }

         return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
               {filteredOOPs.map((oop) => (
                  <UserOOPCard
                     key={oop._id}
                     oop={oop}
                     onRefetch={refetchOOPs}
                  />
               ))}
            </div>
         );
      }

      // Existing applications view
      return renderApplicationsGrid();
   };

   // Update the refresh handler
   const handleRefresh = async () => {
      setIsRefreshing(true);
      try {
         if (viewType === 'oops') {
            await refetchOOPs();
         } else {
            await refetch();
         }
         toast.success(`${viewType === 'oops' ? 'Order of payments' : 'Applications'} refreshed successfully`);
      } catch (error) {
         console.error('Error refreshing:', error);
         toast.error('Failed to refresh data');
      } finally {
         setTimeout(() => setIsRefreshing(false), 500);
      }
   };

   // Action handlers
   const handleEdit = async (id, editedData) => {
      try {
         switch (editedData.applicationType) {
            case 'Certificate of Verification':
               await updateCOVPermit(id, editedData);
               break;
            case 'Chainsaw Registration':
               await updateCSAWPermit(id, editedData);
               break;
            case 'Public Land Tree Cutting Permit':
               await updatePLTCPPermit(id, editedData);
               break;
            case 'Private Tree Plantation Registration':
               await updatePTPRPermit(id, editedData);
               break;
            case 'Private Land Timber Permit':
               await updatePLTPPermit(id, editedData);
               break;
            case 'Tree Cutting and/or Earth Balling Permit':
               await updateTCEBPPermit(id, editedData);
               break;
            default:
               throw new Error('Unknown application type');
         }
         toast.success('Application updated successfully');
         await refetch();
      } catch (error) {
         console.error('Error updating application:', error);
         toast.error('Failed to update application');
      }
   };

   const handleDelete = async (application) => {
      try {
         await deletePermit(application.id);
         toast.success('Application deleted successfully');
         await refetch();
      } catch (error) {
         console.error('Error deleting application:', error);
         toast.error('Failed to delete application');
      }
   };

   const handleUnsubmit = async (application) => {
      try {
         await unsubmitPermit(application.id);
         toast.success('Application unsubmitted successfully');
         await refetch();
      } catch (error) {
         console.error('Error unsubmitting application:', error);
         toast.error('Failed to unsubmit application');
      }
   };

   const handleSubmit = async (application) => {
      try {
         await submitPermit(application.id);
         toast.success('Application submitted successfully');
         await refetch();
      } catch (error) {
         console.error('Error submitting application:', error);
         toast.error('Failed to submit application');
      }
   };

   const handleResubmit = async (application) => {
      try {
         await resubmitPermit(application.id);
         toast.success('Application resubmitted successfully');
         await refetch();
      } catch (error) {
         console.error('Error resubmitting application:', error);
         toast.error('Failed to resubmit application');
      }
   };

   // Date Range Component
   const DateRangeComponent = () => {
      return (
         <Popover>
            <PopoverTrigger asChild>
               <Button
                  variant="outline"
                  className={`w-[240px] justify-start text-left font-normal ${filters.dateRange.from ? 'text-foreground' : 'text-muted-foreground'
                     }`}
               >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {filters.dateRange.from ? (
                     filters.dateRange.to ? (
                        <>
                           {format(filters.dateRange.from, "MMM d, yyyy")} -{" "}
                           {format(filters.dateRange.to, "MMM d, yyyy")}
                        </>
                     ) : (
                        format(filters.dateRange.from, "MMM d, yyyy")
                     )
                  ) : (
                     <span>Filter by date range</span>
                  )}
               </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
               <Calendar
                  initialFocus
                  mode="range"
                  selected={filters.dateRange}
                  onSelect={(range) =>
                     setFilters(prev => ({
                        ...prev,
                        dateRange: range || { from: undefined, to: undefined }
                     }))
                  }
                  numberOfMonths={2}
               />
            </PopoverContent>
         </Popover>
      );
   };

   // Clear filters handler
   const handleClearFilters = () => {
      setFilters({
         searchTerm: '',
         applicationType: 'all',
         dateRange: {
            from: undefined,
            to: undefined
         }
      });
      setActiveStatus('all');
   };

   // Update the filters section to include view type toggle
   const renderFilters = () => (
      <div className="flex flex-col md:flex-row gap-4">
         {/* Search Input - Now First */}
         <div className="w-full md:w-1/3 relative">
            <Input
               placeholder="Search..."
               value={filters.searchTerm}
               onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
               className="w-full dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 pl-10"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
         </div>

         {/* Filters Container */}
         <div className="w-full md:w-2/3 flex flex-col md:flex-row gap-4 md:justify-end">
            {/* View Type Toggle */}
            <Select
               value={viewType}
               onValueChange={(value) => {
                  setViewType(value);
                  setActiveStatus('all'); // Reset status when changing view type
               }}
            >
               <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="View Type" />
               </SelectTrigger>
               <SelectContent>
                  <SelectItem value="applications">Applications</SelectItem>
                  <SelectItem value="oops">Order of Payments</SelectItem>
               </SelectContent>
            </Select>

            {/* Application Type Select */}
            {viewType === 'applications' && (
               <Select
                  value={filters.applicationType}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, applicationType: value }))}
               >
                  <SelectTrigger className="w-[200px]">
                     <SelectValue placeholder="Application Type" />
                  </SelectTrigger>
                  <SelectContent>
                     <SelectItem value="all">All Types</SelectItem>
                     <SelectItem value="Chainsaw Registration">Chainsaw Registration</SelectItem>
                     <SelectItem value="Certificate of Verification">Certificate of Verification</SelectItem>
                     <SelectItem value="Private Tree Plantation Registration">Private Tree Plantation Registration</SelectItem>
                     <SelectItem value="Public Land Tree Cutting Permit">Public Land Tree Cutting Permit</SelectItem>
                     <SelectItem value="Private Land Timber Permit">Private Land Timber Permit</SelectItem>
                     <SelectItem value="Tree Cutting and/or Earth Balling Permit">Tree Cutting and/or Earth Balling Permit</SelectItem>
                  </SelectContent>
               </Select>
            )}

            {/* Status Select - Updated with conditional options */}
            <Select
               value={activeStatus}
               onValueChange={setActiveStatus}
            >
               <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Status" />
               </SelectTrigger>
               <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {viewType === 'applications' ? (
                     // Application Status Options
                     <>
                        <SelectItem value="Draft">Draft</SelectItem>
                        <SelectItem value="Submitted">Submitted</SelectItem>
                        <SelectItem value="In Progress">In Progress</SelectItem>
                        <SelectItem value="Returned">Returned</SelectItem>
                        <SelectItem value="Accepted">Accepted</SelectItem>
                        <SelectItem value="Released">Released</SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                        <SelectItem value="Rejected">Rejected</SelectItem>
                        <SelectItem value="Expired">Expired</SelectItem>
                        <SelectItem value="Renewed">Renewed</SelectItem>
                     </>
                  ) : (
                     // Order of Payment Status Options
                     <>
                        <SelectItem value="Awaiting Payment">Awaiting Payment</SelectItem>
                        <SelectItem value="Manual Payment">Manual Payment</SelectItem>
                        <SelectItem value="Payment Proof Submitted">Payment Proof Submitted</SelectItem>
                        <SelectItem value="Payment Proof Approved">Payment Proof Approved</SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                        <SelectItem value="Issued OR">Issued OR</SelectItem>
                     </>
                  )}
               </SelectContent>
            </Select>

            {/* Date Range Component */}
            <DateRangeComponent />

            {/* Clear Filters Button */}
            <Button
               variant="outline"
               onClick={handleClearFilters}
               className="px-4 h-10 whitespace-nowrap dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
            >
               Clear Filters
            </Button>
         </div>
      </div>
   );

   // Update renderApplicationsGrid function
   const renderApplicationsGrid = () => {
      if (loading) {
         return (
            <div className="flex justify-center items-center py-12">
               <RefreshCw className="h-8 w-8 animate-spin text-green-600" />
               <p className="ml-3 text-gray-500">Loading applications...</p>
            </div>
         );
      }

      if (error) {
         return (
            <div className="flex flex-col items-center justify-center py-12">
               <XCircle className="h-12 w-12 text-red-500 mb-4" />
               <p className="text-gray-600">Error loading applications: {error.message}</p>
            </div>
         );
      }

      if (!visibleApplications || visibleApplications.length === 0) {
         return (
            <div className="flex flex-col items-center justify-center py-12">
               <FileX className="h-12 w-12 text-gray-400 mb-4" />
               <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No applications found</h3>
               <p className="text-sm text-gray-500">Try adjusting your filters or create a new application</p>
            </div>
         );
      }

      return (
         <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
               {visibleApplications.map((application) => (
                  <ClientApplicationCard
                     key={application.id}
                     application={application}
                     onEdit={handleEdit}
                     onDelete={handleDelete}
                     onUnsubmit={handleUnsubmit}
                     onSubmit={handleSubmit}
                     onResubmit={handleResubmit}
                     fetchCOVPermit={fetchCOVPermit}
                     fetchCSAWPermit={fetchCSAWPermit}
                     fetchPLTCPPermit={fetchPLTCPPermit}
                     fetchPTPRPermit={fetchPTPRPermit}
                     fetchPLTPPermit={fetchPLTPPermit}
                     fetchTCEBPPermit={fetchTCEBPPermit}
                  />
               ))}
            </div>

            {/* Loading More Indicator - Only show if there are more to load */}
            {visibleApplications.length < filteredApplications.length && (
               <div
                  ref={loadMoreRef}
                  className="flex justify-center items-center py-8 mt-4"
               >
                  <div className="flex items-center gap-2 text-gray-500">
                     <RefreshCw className={`h-5 w-5 ${isLoadingMore ? 'animate-spin' : ''}`} />
                     <span className="text-sm">
                        {isLoadingMore ? 'Loading more applications...' : 'Scroll to load more'}
                     </span>
                  </div>
               </div>
            )}
         </>
      );
   };

   // Show loading state
   if (loading && visibleCards === CARDS_PER_PAGE) {
      return (
         <div className="flex flex-col items-center justify-center min-h-screen p-4">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500 mb-4"></div>
            <p className="text-gray-600">Loading your applications...</p>
         </div>
      );
   }

   // Show error state
   if (error) {
      return (
         <div className="flex flex-col items-center justify-center min-h-screen p-4">
            <XCircle className="h-12 w-12 text-red-500 mb-4" />
            <p className="text-gray-600">Error loading applications. Please try again later.</p>
            <Button onClick={handleRefresh} variant="outline" className="mt-4">
               <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
               Retry
            </Button>
         </div>
      );
   }

   return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
         {/* Main Container */}
         <div className="max-w-[97rem] mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Navigation */}
            <nav className="mb-8">
               <Link
                  to="/home"
                  className="inline-flex items-center px-4 py-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors duration-200"
               >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back to Home
               </Link>
            </nav>

            {/* Content Container */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
               {/* Header Section */}
               <div className="border-b border-gray-100 dark:border-gray-700 px-6 py-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                     <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Applications</h1>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                           Track and manage all your permit applications in one place
                        </p>
                     </div>
                     <div className="flex items-center gap-2">
                        <Button
                           variant="outline"
                           size="sm"
                           onClick={() => toast.info('Help tour coming soon!')}
                           className="bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 transition-colors duration-200"
                        >
                           <HelpCircle className="h-4 w-4 mr-2" />
                           Help
                        </Button>
                        <Button
                           variant="outline"
                           size="sm"
                           onClick={handleRefresh}
                           disabled={isRefreshing}
                           className="bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 transition-colors duration-200"
                        >
                           <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                           Refresh
                        </Button>
                     </div>
                  </div>
               </div>

               {/* Filters Section */}
               <div className="p-6 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                  {renderFilters()}
               </div>

               {/* Main Content Section */}
               <div className="p-6">
                  {renderMainContent()}
               </div>
            </div>
         </div>
      </div>
   );
};

export default ClientApplicationsPage;